// RewardedHint (§ ads) — "Watch an ad → reveal the next recipe", capped at 3/day.
// Uses rewardedAdService (H5 Games Ads on web) with a graceful fallback: if no
// rewarded ad can be served yet, the hint is still granted so players are never
// hard-blocked. Reveals the full recipe of an element you can forge right now.
import React, { useState } from "react";
import { Modal } from "../ui/primitives";
import ElementOrb from "../ui/ElementOrb";
import { useElementStore } from "../../store/elementStore";
import { showRewarded, RewardOutcome, rewardedAdsEnabled } from "../../services/rewardedAdService";
import { getAdHintState, consumeAdHint, getNextRecipeHint, RecipeHint } from "../../game/adHints";
import { playSound } from "../../services/soundService";
import { triggerHaptic } from "../../services/capacitorService";

const RewardedHint: React.FC = () => {
  const unlocked = useElementStore((s) => s.unlocked);
  const [state, setState] = useState(() => getAdHintState());
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<RecipeHint | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const disabled = busy || state.remaining <= 0;

  const onWatch = async () => {
    if (disabled) return;
    // Nothing new to reveal? Don't spend an ad/credit on it.
    const next = getNextRecipeHint(unlocked);
    if (!next) {
      setNote("You can already make everything within reach — combine to unlock more first!");
      return;
    }
    setBusy(true);
    setNote(null);
    let outcome: RewardOutcome = "unavailable";
    try {
      outcome = await showRewarded("full-hint");
    } catch {
      outcome = "unavailable";
    }
    setBusy(false);

    if (outcome === "dismissed") {
      setNote("Ad closed early — watch the full ad to earn a hint.");
      return;
    }
    if (outcome === "unavailable") {
      setNote("No ad available right now — please try again shortly.");
      return;
    }
    // Only a fully-viewed rewarded ad grants the hint (and spends a daily credit).
    triggerHaptic("success");
    playSound("unlock");
    const fresh = getNextRecipeHint(unlocked) || next;
    setState(consumeAdHint());
    setHint(fresh);
  };

  // Hidden entirely until rewarded ads are enabled/approved.
  if (!rewardedAdsEnabled()) return null;

  return (
    <>
      <div style={{ marginTop: 14, display: "flex", justifyContent: "center" }}>
        <button
          className="mc-tap"
          onClick={onWatch}
          disabled={disabled}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            minHeight: 44,
            padding: "0 16px",
            borderRadius: 12,
            border: "1px solid rgba(245,183,64,0.4)",
            background: disabled ? "var(--fill)" : "rgba(245,183,64,0.12)",
            color: disabled ? "var(--text-3)" : "#F5B740",
            fontSize: 13,
            fontWeight: 700,
            cursor: disabled ? "default" : "pointer",
          }}
          aria-label="Watch an ad to reveal a recipe"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M10 9l5 3-5 3z" fill="currentColor" />
          </svg>
          {state.remaining > 0 ? `Watch ad → reveal a recipe (${state.remaining} left today)` : "Daily hints used — back tomorrow"}
        </button>
      </div>
      {note && <div style={{ marginTop: 8, textAlign: "center", fontSize: 12, color: "var(--text-3)" }}>{note}</div>}

      <Modal open={!!hint} onClose={() => setHint(null)} title="Recipe revealed">
        {hint && (
          <div style={{ textAlign: "center" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, margin: "6px 0 14px" }}>
              <ElementOrb id={hint.ingredients[0]} size={48} />
              <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700, color: "var(--text-3)" }}>+</span>
              <ElementOrb id={hint.ingredients[1]} size={48} />
              <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700, color: "var(--text-3)" }}>=</span>
              <ElementOrb id={hint.resultId} size={48} withRing />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {hint.ingredientNames[0]} + {hint.ingredientNames[1]} = {hint.resultName}
            </div>
            <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-2)" }}>Stage these two in the Forge and combine to discover it.</div>
            <button className="mc-tap" onClick={() => setHint(null)} style={{ marginTop: 18, minHeight: 46, width: "100%", border: "none", borderRadius: 12, background: "#65A30D", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Got it</button>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RewardedHint;
